import React, { useState, useEffect, useRef } from 'react';
import { Maximize2, Minimize2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { SavedHMILayout, renderHMIComponent, resolveFlowSourceOn, resolveFlowSourceIntensity, HMIComponent } from './HMIComponents';
import { getWebSocketClient } from '../../utils/websocket-client';
import { API_BASE } from '../../utils/api-base';

interface HMIViewerProps {
  clientId: string;
  token: string;
  pollingIntervalMs?: number;
}

// The client portal fully unmounts each screen on tab switch (see ClientPortal's
// AnimatePresence), so component state doesn't survive leaving and reopening the
// HMI tab. Without this, every reopen started tagValues from {} and every valve/
// pump/motor sat in its neutral "unknown" state until a fresh fetch/WS tick came
// in — reads as the screen not showing real state instantly. Caching the last
// known values per client at module scope (survives remounts, cleared on full
// page reload) lets a reopen paint with the last known state immediately while
// fresh data loads in the background.
const lastKnownTagValuesByClient: Record<string, Record<string, number>> = {};

export function HMIViewer({ clientId, token, pollingIntervalMs = 5000 }: HMIViewerProps) {
  const [layouts, setLayouts]               = useState<SavedHMILayout[]>([]);
  const [selectedId, setSelectedId]         = useState<string | null>(null);
  const [tagValues, setTagValues]           = useState<Record<string, number>>(() => lastKnownTagValuesByClient[clientId] || {});
  const [loading, setLoading]               = useState(true);
  const [fullscreen, setFullscreen]         = useState(false);

  const applyTagValues = (updater: (prev: Record<string, number>) => Record<string, number>) => {
    setTagValues(prev => {
      const next = updater(prev);
      lastKnownTagValuesByClient[clientId] = next;
      return next;
    });
  };

  const currentLayout = layouts.find(l => l.id === selectedId) ?? null;
  const currentPageIndex = layouts.findIndex(l => l.id === selectedId);
  const goToPage = (delta: number) => {
    if (layouts.length === 0) return;
    const baseIdx = currentPageIndex === -1 ? 0 : currentPageIndex;
    const nextIdx = Math.max(0, Math.min(layouts.length - 1, baseIdx + delta));
    setSelectedId(layouts[nextIdx].id);
  };

  // ---- Nav Button navigation (in-canvas "jump to screen" / "go back" clicks) ----
  // Tracks the trail of screens actually visited — via Nav Button clicks, the
  // toolbar page tabs, or the prev/next arrows — so a "Go Back" button returns to
  // wherever the operator really came from. This used to only push onto the trail
  // from inside handleNavButtonClick's forward branch, so leaving a screen via the
  // page tabs/arrows (both set selectedId directly, bypassing this function) never
  // got recorded — the next Back click then popped a stale entry and landed on the
  // wrong screen. Tracking every selectedId change in one place, regardless of how
  // it happened, keeps the trail accurate no matter which control the operator uses.
  const screenHistoryRef = useRef<string[]>([]);
  const prevSelectedIdRef = useRef<string | null>(null);
  const isBackNavRef = useRef(false);
  useEffect(() => {
    const prev = prevSelectedIdRef.current;
    if (prev && prev !== selectedId && !isBackNavRef.current) {
      screenHistoryRef.current.push(prev);
    }
    isBackNavRef.current = false;
    prevSelectedIdRef.current = selectedId;
  }, [selectedId]);

  const handleNavButtonClick = (comp: HMIComponent) => {
    const target = comp.navTargetLayoutId;
    if (!target) return;
    if (target === '__back__') {
      const prev = screenHistoryRef.current.pop();
      // Don't record this transition as a forward move — otherwise the screen we're
      // leaving would get pushed right back on, turning "Back" into a toggle that
      // can never go further back than one screen.
      if (prev) { isBackNavRef.current = true; setSelectedId(prev); }
      return;
    }
    if (!layouts.some(l => l.id === target)) return; // target screen no longer exists
    setSelectedId(target);
  };

  // ---- Fetch layouts ----
  const fetchLayouts = () => {
    setLoading(true);
    fetch(`${API_BASE}/api/clients/${clientId}/hmi-layouts`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => {
        const list: SavedHMILayout[] = d.layouts || [];
        setLayouts(list);
        if (list.length > 0 && !selectedId) setSelectedId(list[0].id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchLayouts(); }, [clientId]);

  // ---- WebSocket for live PLC data ----
  useEffect(() => {
    let isMounted = true;
    const ws = getWebSocketClient(token, { clientId, pollingIntervalMs });

    const handleLiveData = (data: any) => {
      if (!isMounted) return;
      const plcId = data.plcId;
      if (!plcId) return;
      const values: Record<string, number | boolean> = data.values || {};
      applyTagValues(prev => {
        const next = { ...prev };
        Object.entries(values).forEach(([tagName, val]) => {
          // Bit/coil tags (e.g. Mitsubishi M-registers) arrive as a plain JS boolean
          // rather than a number — coerce to 0/1 so bound Valve/Pump/Motor widgets
          // (which compare against `onValue` numerically) still pick up the state.
          if (typeof val === 'number') next[`${plcId}::${tagName}`] = val;
          else if (typeof val === 'boolean') next[`${plcId}::${tagName}`] = val ? 1 : 0;
        });
        return next;
      });
    };

    ws.on('plc_live_data', handleLiveData);
    // Registering a handler above does NOT open the connection — every other live
    // screen (LivePLCData, ScannerDashboard, PLCManagement, …) calls connect() right
    // after wiring up its handlers. HMIViewer was missing this, so unless some other
    // component happened to already be mounted (the WS/SSE client is a shared
    // singleton) and had opened it, HMI never received a single live push and sat on
    // the REST fallback below only — which is why it could look "not realtime" right
    // after login if HMI was the first/only screen opened in the session.
    ws.connect();
    return () => {
      isMounted = false;
      ws.off('plc_live_data', handleLiveData);
    };
  }, [clientId, token, pollingIntervalMs]);

  // ---- REST polling fallback ----
  // On hosts without a direct bridge WebSocket (VPS/SSE relay mode), plc_live_data
  // events aren't guaranteed to arrive promptly. Poll the same lightweight
  // "latest values" endpoint Live PLC Data uses so bound tags never sit stale.
  // Fixed, fast cadence — deliberately NOT derived from the client's configured
  // pollingIntervalMs, which is the *historical data write* interval (often minutes,
  // 5 min by default) and has nothing to do with how fast the live view should
  // refresh. Using it here (via Math.max as only a floor, with no ceiling) used to
  // let this fallback poll as slowly as once every 5 minutes when the WebSocket
  // wasn't connected — this is a safety net and needs to stay fast regardless.
  const HMI_LIVE_POLL_MS = 3000;
  useEffect(() => {
    let isMounted = true;

    const fetchLatestValues = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/clients/${clientId}/plcs/latest-values`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json();
        if (!isMounted || !response.ok) return;
        const tags = Array.isArray(payload?.tags) ? payload.tags : [];
        if (tags.length === 0) return;
        applyTagValues(prev => {
          const next = { ...prev };
          for (const tag of tags) {
            if (typeof tag.value === 'number') next[`${tag.plcId}::${tag.name}`] = tag.value;
            else if (typeof tag.value === 'boolean') next[`${tag.plcId}::${tag.name}`] = tag.value ? 1 : 0;
          }
          return next;
        });
      } catch {
        // Keep showing the last known values if a poll cycle fails.
      }
    };

    fetchLatestValues();
    const intervalId = setInterval(fetchLatestValues, HMI_LIVE_POLL_MS);
    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [clientId, token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400 text-sm">
        <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading HMI…
      </div>
    );
  }

  if (layouts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500 text-sm text-center px-8">
        No HMI layouts have been configured for this site. Ask your administrator to create one.
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${fullscreen ? 'fixed inset-0 z-50 bg-slate-950' : 'h-full'}`}>
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-2 bg-slate-900 border-b border-slate-800 shrink-0">
        {layouts.length > 1 ? (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => goToPage(-1)}
              disabled={currentPageIndex <= 0}
              title="Previous page"
              className="p-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white disabled:opacity-30 disabled:hover:bg-slate-800 disabled:hover:text-slate-400"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="flex gap-1 flex-wrap">
              {layouts.map(l => (
                <button
                  key={l.id}
                  onClick={() => setSelectedId(l.id)}
                  className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
                    l.id === selectedId
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                  }`}
                >
                  {l.name}
                </button>
              ))}
            </div>

            <button
              onClick={() => goToPage(1)}
              disabled={currentPageIndex === -1 || currentPageIndex === layouts.length - 1}
              title="Next page"
              className="p-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white disabled:opacity-30 disabled:hover:bg-slate-800 disabled:hover:text-slate-400"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            <span className="text-[10px] text-slate-500 tabular-nums ml-0.5">
              {currentPageIndex === -1 ? '—' : currentPageIndex + 1} / {layouts.length}
            </span>
          </div>
        ) : (
          <span className="text-sm font-semibold text-slate-200">{layouts[0]?.name}</span>
        )}

        {/* Live indicator */}
        <div className="flex items-center gap-1.5 ml-2">
          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[10px] text-slate-500">LIVE</span>
        </div>

        {/* Fullscreen toggle */}
        <button
          onClick={() => setFullscreen(f => !f)}
          className="ml-auto p-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-400"
          title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>

      {/* Canvas */}
      {currentLayout && (
        <div className="flex-1 overflow-auto bg-slate-950 flex items-center justify-center p-2">
          <svg
            viewBox={`0 0 ${currentLayout.layout.width ?? 1200} ${currentLayout.layout.height ?? 700}`}
            style={{
              background: currentLayout.layout.background || '#0f172a',
              borderRadius: 8,
              // Lock the box to the exact same aspect ratio as the Builder's canvas
              // (see HMIBuilder.tsx) instead of stretching to 100%/100% and relying on
              // object-fit to letterbox it back — object-fit isn't reliably honored on a
              // root <svg> across browsers, and a mismatched box scales/positions the
              // drawing differently than what was designed, which is what made the
              // Viewer render smaller/off from the Builder preview.
              aspectRatio: `${currentLayout.layout.width ?? 1200} / ${currentLayout.layout.height ?? 700}`,
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              display: 'block',
              border: '1px solid #1e293b',
            }}
          >
            {(currentLayout.layout.components ?? []).map((comp: HMIComponent) => {
              const key   = comp.plcId && comp.tagName ? `${comp.plcId}::${comp.tagName}` : null;
              const value = key != null ? (tagValues[key] ?? null) : null;

              // Pipes can mirror another component's ON/OFF state instead of their own
              // tag binding (e.g. "only flows when this valve is open") — follows a
              // chain of references (pipe -> pipe -> valve) if there is one. The
              // continuous intensity version lets a Flow Control Valve source make
              // the pipe's flow visibly speed up/brighten with its 0-100% position.
              const getRawFlowValue = (c: HMIComponent) => {
                const k = c.plcId && c.tagName ? `${c.plcId}::${c.tagName}` : null;
                return k != null ? (tagValues[k] ?? null) : null;
              };
              const flowSourceOn = comp.flowSourceId
                ? resolveFlowSourceOn(comp, currentLayout.layout.components ?? [], getRawFlowValue)
                : undefined;
              const flowIntensity = comp.flowSourceId
                ? resolveFlowSourceIntensity(comp, currentLayout.layout.components ?? [], getRawFlowValue)
                : undefined;

              const cx  = comp.x + comp.width / 2;
              const cy  = comp.y + comp.height / 2;
              const rot = comp.rotation ?? 0;

              const isNavButton = comp.type === 'nav-button';

              return (
                <g
                  key={comp.id}
                  transform={rot !== 0
                    ? `rotate(${rot},${cx},${cy}) translate(${comp.x},${comp.y})`
                    : `translate(${comp.x},${comp.y})`}
                  onClick={isNavButton ? () => handleNavButtonClick(comp) : undefined}
                  style={isNavButton ? { cursor: 'pointer' } : undefined}
                >
                  {renderHMIComponent(comp, value, undefined, flowSourceOn, flowIntensity)}
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
}
